import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
interface CertificationItem {
  /** Sequential marker so all four certifications remain individually countable */
  index: string;
  label: string;
  body: string;
}
interface CertificationTheme {
  heading: string;
  intro: string;
  items: CertificationItem[];
  closing: string;
}
const themes: CertificationTheme[] = [{
  heading: 'Quality & Process Maturity',
  intro: 'Two certifications reflect the maturity and consistency of how we deliver:',
  items: [{
    index: '01',
    label: 'ISO 9001:2015',
    body: 'Certifies our quality management system, ensuring standardized, audited processes are applied consistently across every project.'
  }, {
    index: '02',
    label: 'CMMI Level 3 (Development & Services)',
    body: 'Certifies that our development and service delivery processes are well-defined, consistently applied, and tied to measurable performance outcomes.'
  }],
  closing: 'Together, these standards give our federal and government customers confidence in predictable execution and drive our continuous improvement efforts to strengthen delivery outcomes over time.'
}, {
  heading: 'Security & Compliance',
  intro: 'Two additional certifications validate how we protect the information entrusted to us:',
  items: [{
    index: '03',
    label: 'ISO/IEC 27001:2022',
    body: 'Certifies our information security management system, built to protect customer and program data through formal, audited security controls.'
  }, {
    index: '04',
    label: 'CMMC Level 2',
    body: 'Certifies our cybersecurity practices for handling Controlled Unclassified Information (CUI), meeting DoD requirements for defense contract work.'
  }],
  closing: 'Together, these standards position New River to meet the security requirements of DoD and federal contract work with confidence.'
}];
const clientBenefits = ['Every engagement follows a standardized, auditable quality management process', 'Only qualified personnel are assigned to federal and government projects', 'Client and program data is protected under a certified information security management system', 'Our processes are continuously reviewed and improved against measurable performance outcomes'];
export function CertificationsPage() {
  return <div className="w-full" data-id="element-1064" data-mp="6mwqie3mkp9b">
      {/* SECTION 1 — HERO */}
      <section className="section-navy hero-grid-pattern" data-id="element-1065" data-mp="6kaiyegqi9r3">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-1066" data-mp="kebkffz2mav5">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-1067" data-mp="flqtcm7jubvb">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl mx-auto" data-id="element-1068" data-mp="3o23gvmpzsrbm">
              Certifications &amp; Compliance
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-1069" data-mp="3jyxewr9wxmay"></div>
            <p className="text-xl text-nrs-light font-bold max-w-3xl mx-auto leading-relaxed" data-id="element-1070" data-mp="39vi8jxxswjmr">
              We understand the criticality of the systems and processes that
              manage the quality of work we deliver for our customers. The way
              we hold ourselves accountable to established standards ensures
              rigor and consistency across every engagement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — CERTIFICATIONS STATEMENT */}
      <section className="section-white" data-id="element-1071" data-mp="1gi1wsxq6ds2">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1072" data-mp="3ipwnfzef5s5c">
          <p className="max-w-3xl mx-auto text-center text-xl md:text-2xl text-nrs-navy font-heading font-semibold leading-relaxed" data-id="element-1073" data-mp="3irr8ddpoxk9f">
            That commitment is backed by four formal certifications — spanning
            quality management, information security, and process maturity —
            that our customers can verify and rely on: ISO 9001:2015, ISO/IEC
            27001:2022, CMMI Level 3 (Development &amp; Services), and CMMC
            Level 2.
          </p>
        </div>
      </section>

      {/* SECTION 3 — THEMED CERTIFICATION DETAIL */}
      <section className="section-light-blue" data-id="element-1074" data-mp="2irfm05e5345y">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1075" data-mp="2drl0yih1p8st">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1076" data-mp="2dpgkl11ymhbn">
            {themes.map(theme => <div key={theme.heading} className="card flex flex-col" data-id="element-1077" data-mp="3bejyswjh4msa">
                <h2 className="text-2xl font-bold mb-3" data-id="element-1078" data-mp="36ha0ere3x0sb">{theme.heading}</h2>
                <div className="h-1 w-12 bg-nrs-orange rounded-full mb-5" data-id="element-1079" data-mp="31gmbqcdcow19"></div>
                <p className="text-nrs-gray leading-relaxed mb-6" data-id="element-1080" data-mp="2wi2om8n0te7b">
                  {theme.intro}
                </p>

                <div className="divide-y divide-gray-200 border-y border-gray-200 mb-6" data-id="element-1081" data-mp="1q2i7mdd1trih">
                  {theme.items.map(item => <div key={item.label} className="py-5 flex gap-4" data-id="element-1082" data-mp="1l2mbf0alf4dh">
                      <span className="flex-shrink-0 font-heading font-bold text-sm text-nrs-orange pt-0.5" aria-hidden="true" data-id="element-1083" data-mp="l4txu979dn2">
                        {item.index}
                      </span>
                      <div data-id="element-1084" data-mp="aj14ajubc4k4">
                        <h3 className="font-heading font-bold text-lg text-nrs-navy mb-2 leading-snug" data-id="element-1085" data-mp="air9miybp983">
                          {item.label}
                        </h3>
                        <p className="text-nrs-gray leading-relaxed" data-id="element-1086" data-mp="k8ubz9rc3zj3">
                          {item.body}
                        </p>
                      </div>
                    </div>)}
                </div>

                <p className="text-nrs-gray leading-relaxed mt-auto" data-id="element-1087" data-mp="t90oasbaqtgo">
                  {theme.closing}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHAT THIS MEANS FOR OUR CLIENTS */}
      <section className="section-white" data-id="element-1088" data-mp="2v2gg3sj4eed8">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1089" data-mp="2zzqdbucr3ubm">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1090" data-mp="3022u446rvyn3">
            What This Means for Our Clients
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-1091" data-mp="2nxp4bhhdul8l"></div>

          <ul className="max-w-3xl mx-auto space-y-4" data-id="element-1092" data-mp="2o01sicdqhru3">
            {clientBenefits.map(benefit => <li key={benefit} className="flex items-start gap-4 bg-nrs-light p-5 rounded-md" data-id="element-1093" data-mp="31vdmuinz9gah">
                <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-1094" data-mp="2wx2g4gszblwh" />
                <span className="text-lg text-nrs-navy leading-relaxed" data-id="element-1095" data-mp="3awllx2ybil5m">
                  {benefit}
                </span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* SECTION 5 — CLOSING CTA */}
      <section className="section-light-blue" data-id="element-1096" data-mp="3kq2habe3s8be">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-1097" data-mp="3folhykb9otwx">
          <h2 className="text-2xl font-bold mb-8" data-id="element-1098" data-mp="3ulma2oxdeqvx">
            See How Our Certifications Support Our Delivery
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1099" data-mp="3poca4fccuwtu">
            <Link to="/about-us/contract-vehicles" className="btn-primary" data-id="element-1100" data-mp="d2x1x5qzhp0w">
              View Our Contract Vehicles
            </Link>
            <Link to="/contact-us" className="btn-secondary" data-id="element-1101" data-mp="h4ieepzlw1ak">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>;
}